import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Field, Pick } from "@/components/inquiries/InquiryFormDialog";

export const StatusDialog = ({ open, inquiry, settings, onClose, onSaved }) => {
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setStatus(inquiry.status); setReason(""); setNote(""); } }, [open, inquiry]);
  const kind = settings.statuses.find((s) => s.key === status)?.kind;
  const options = settings.statuses.filter((s) => s.kind !== "converted" || inquiry.links?.length).map((s) => ({ value: s.key, label: s.label }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/inquiries/${inquiry.id}`, { status, closure_reason: kind === "closed" ? reason : null, closure_note: note || null });
      toast.success("Status updated"); notifyInquiriesChanged(); onSaved(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="status-dialog">
        <DialogHeader><DialogTitle>Change status</DialogTitle>
          <DialogDescription>Every change is recorded in the journey. “Converted” is set automatically when you convert.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Status"><Pick testid="status-select" value={status} onChange={setStatus} options={options} /></Field>
          {kind === "closed" && <Field label="Closure reason *"><Pick testid="closure-reason" value={reason} onChange={setReason} options={settings.closure_reasons} /></Field>}
          <Field label="Note"><Textarea data-testid="status-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="status-save" disabled={busy || status === inquiry.status || (kind === "closed" && !reason)} onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

import { localToday as today, notifyInquiriesChanged } from "@/lib/inquiry";

const TaskFields = ({ d, set, lists }) => (
  <>
    <Field label="Task name *" className="col-span-2"><Input data-testid="conv-name" value={d.name} onChange={(e) => set("name", e.target.value)} /></Field>
    <Field label="Task date"><Input data-testid="conv-task-date" type="date" value={d.task_date} onChange={(e) => set("task_date", e.target.value)} /></Field>
    <Field label="Due date"><Input data-testid="conv-due" type="date" value={d.due_date || ""} onChange={(e) => set("due_date", e.target.value || null)} /></Field>
    <Field label="Assign to"><Pick testid="conv-assignee" value={d.assignee_id} onChange={(v) => set("assignee_id", v)} options={lists.people} placeholder="Me" /></Field>
    <Field label="Task type"><Pick testid="conv-task-type" value={d.task_type} onChange={(v) => set("task_type", v)} options={lists.taskTypes} /></Field>
  </>
);

const ObligationFields = ({ d, set, lists }) => (
  <>
    <Field label="Obligation name *" className="col-span-2"><Input data-testid="conv-name" value={d.name} onChange={(e) => set("name", e.target.value)} /></Field>
    <Field label="Category"><Pick testid="conv-ob-category" value={d.category} onChange={(v) => set("category", v)} options={lists.categories} /></Field>
    <Field label="Type"><Pick testid="conv-ob-type" value={d.type} onChange={(v) => set("type", v)} options={lists.obTypes} /></Field>
    <Field label="Frequency"><Pick testid="conv-ob-frequency" value={d.frequency} onChange={(v) => set("frequency", v)} options={["one-time", "monthly", "quarterly", "half-yearly", "yearly"].map((x) => ({ value: x, label: x.replace("-", " ") }))} /></Field>
    <Field label="Amount"><Input data-testid="conv-ob-amount" type="number" value={d.amount ?? ""} onChange={(e) => set("amount", e.target.value === "" ? null : Number(e.target.value))} /></Field>
    <Field label="First due date"><Input data-testid="conv-ob-due" type="date" value={d.next_due_date} onChange={(e) => set("next_due_date", e.target.value)} /></Field>
  </>
);

export const ConvertDialog = ({ open, inquiry, settings, onClose, onSaved }) => {
  const [target, setTarget] = useState("task");
  const [d, setD] = useState({});
  const [lists, setLists] = useState({ people: [], taskTypes: [], categories: [], obTypes: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const t = settings.conversion_targets[0] || "task";
    setTarget(t);
    const g = (u) => api.get(u).then((r) => r.data).catch(() => []);
    Promise.all([g("/users/assignable?team=1"), g("/task-types"), g("/categories"), g("/obligation-types")]).then(([p, tt, c, ot]) =>
      setLists({ people: p.map((x) => ({ value: x.id, label: x.name })), taskTypes: tt.map((x) => x.name), categories: c.map((x) => x.name), obTypes: ot.map((x) => x.name) }));
  }, [open, settings]);
  useEffect(() => {
    if (!open) return;
    const desc = `From ${inquiry.number}: ${inquiry.subject}${inquiry.description ? `\n\n${inquiry.description}` : ""}`;
    setD(target === "task"
      ? { name: inquiry.subject, task_date: today(), due_date: null, assignee_id: inquiry.assigned_to, task_type: lists.taskTypes[0] || "Retainership", client_name: inquiry.organisation || inquiry.contacts?.[0]?.name || null, entity: inquiry.entity || null, description: desc }
      : { name: inquiry.subject, category: lists.categories[0] || "", type: lists.obTypes[0] || "", frequency: "one-time", amount: null, currency: "INR", start_date: today(), next_due_date: today(), entity: inquiry.entity || null, notes: desc });
  }, [open, target, inquiry, lists]);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const data = target === "obligation" ? { ...d, start_date: d.next_due_date } : d;
      const r = await api.post(`/inquiries/${inquiry.id}/convert`, { target, data });
      toast.success(`Created ${target} and linked to ${inquiry.number}`); notifyInquiriesChanged(); onSaved(r.data.inquiry);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="convert-dialog">
        <DialogHeader><DialogTitle>Convert {inquiry.number}</DialogTitle>
          <DialogDescription>Creates the record, links it both ways and marks the inquiry Converted. The journey is kept.</DialogDescription></DialogHeader>
        <div className="flex gap-2 mb-1">
          {settings.conversion_targets.map((t) => (
            <button type="button" key={t} data-testid={`conv-target-${t}`} onClick={() => setTarget(t)}
              className={`px-3 py-1.5 text-sm rounded-md border capitalize ${target === t ? "bg-blue-700 text-white border-blue-700" : "border-slate-200 text-slate-700"}`}>{t}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {target === "task" ? <TaskFields d={d} set={set} lists={lists} /> : <ObligationFields d={d} set={set} lists={lists} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="conv-save" disabled={busy || !d.name} onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white">{busy ? "Creating…" : `Create ${target}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
